import React, { useState, useEffect } from 'react';
import { Check, Loader2, Send } from 'lucide-react';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { ACCESS_OPTIONS } from './visualizationConstants';

const API = process.env.REACT_APP_BACKEND_URL;

const ReviewAccessModal = ({ visualization, onClose, onPublish }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();

  const [publishing, setPublishing] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [accessControl, setAccessControl] = useState(visualization?.access_control || 'public');
  const [allowedUserIds, setAllowedUserIds] = useState(visualization?.allowed_user_ids || []);

  useEffect(() => {
    if (accessControl === 'selected_users' && orgUsers.length === 0) {
      setLoadingUsers(true);
      fetch(`${API}/api/visualizations/meta/org-users`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : [])
        .then(data => setOrgUsers(data))
        .catch(() => {})
        .finally(() => setLoadingUsers(false));
    }
  }, [accessControl, orgUsers.length]);

  const toggleUser = (id) =>
    setAllowedUserIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await fetch(`${API}/api/visualizations/${visualization.visualization_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ access_control: accessControl, allowed_user_ids: allowedUserIds }),
      });
      if (!res.ok) {
        toast.error('Failed to save access settings');
        setPublishing(false);
        return;
      }
      await onPublish();
    } catch (e) {
      console.error('Publish error:', e);
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="review-access-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-green-600" />
            Review Access
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-6">
          <div><Label>Who can view this visualization?</Label><p className={cn("text-xs mt-1 mb-4", colors.textMuted)}>Control access to the published visualization.</p></div>

          <div className="space-y-3">
            {ACCESS_OPTIONS.map(option => {
              const Icon = option.icon;
              const isSelected = accessControl === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setAccessControl(option.value)}
                  className={cn(
                    "w-full p-4 rounded-lg border text-left transition-all",
                    isSelected
                      ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                      : cn(colors.borderPrimary, colors.bgSecondary, "hover:border-purple-500/50")
                  )}
                  data-testid={`review-access-${option.value}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      isSelected ? "bg-purple-500 text-white" : "bg-gray-100 dark:bg-gray-800"
                    )}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className={cn("font-medium", colors.textPrimary)}>{option.label}</p>
                      <p className={cn("text-sm", colors.textMuted)}>{option.description}</p>
                    </div>
                    <div className={cn(
                      "w-5 h-5 rounded-full border flex items-center justify-center",
                      isSelected ? "bg-purple-500 border-purple-500" : colors.borderPrimary
                    )}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {accessControl === 'selected_users' && (
            <div className="space-y-3 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
              <Label>Select Users</Label>
              {loadingUsers ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading users...
                </div>
              ) : orgUsers.length === 0 ? (
                <p className={cn("text-sm", colors.textMuted)}>No other users in your organization.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {orgUsers.map(user => {
                    const isSelected = allowedUserIds.includes(user.user_id);
                    return (
                      <button
                        key={user.user_id}
                        type="button"
                        onClick={() => toggleUser(user.user_id)}
                        className={cn(
                          "w-full p-3 rounded-lg border text-left transition-all flex items-center gap-3",
                          isSelected
                            ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                            : cn(colors.borderPrimary, colors.bgSecondary)
                        )}
                        data-testid={`review-user-${user.user_id}`}
                      >
                        <div className={cn(
                          "w-5 h-5 rounded border flex items-center justify-center",
                          isSelected ? "bg-purple-500 border-purple-500" : colors.borderPrimary
                        )}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn("font-medium text-sm truncate", colors.textPrimary)}>
                            {user.name || user.email}
                          </p>
                          {user.name && (
                            <p className={cn("text-xs truncate", colors.textMuted)}>{user.email}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              <p className={cn("text-sm", colors.textSecondary)}>
                {allowedUserIds.length} user{allowedUserIds.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="review-access-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={publishing}
            className="gap-1 bg-green-600 hover:bg-green-700"
            data-testid="review-access-publish-btn"
          >
            {publishing ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Publishing...</>
            ) : (
              <><Send className="w-4 h-4" /> Publish</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewAccessModal;
